import { InlineAlertType } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import useReactivationPrefillData from 'features/reactivation/hooks/useReactivationPrefillData';
import reviewInfoHelper from 'features/reactivation/screens/ReviewInfo/ReviewInfo.helpers';
import { mapPrefillDataToCustomerInfo } from 'features/reactivation/utils/mapPrefillDataToCustomerInfo';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import {
  ErrorScreen,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const ReviewInfo = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('reactivation', {
    keyPrefix: 'reviewInfo',
  });
  const [validationError, setValidationError] = useState<string>('');

  const insets = useSafeAreaInsets();

  const {
    prefillData,
    prefillDataFetching,
    prefillDataError,
    refetch: refetchPrefillData,
    reactivationApplicationId,
  } = useReactivationPrefillData({ requestPolicy: 'network-only' });

  useViewDidFocusScreenAnalyticsEvent(
    reactivationApplicationId
      ? {
          key: 'review-info-reactivation',
          customAttributes: {
            reactivation_flow: 'true',
            reactivation_id: reactivationApplicationId,
          },
        }
      : undefined,
  );

  const mappedCustomerInfo = mapPrefillDataToCustomerInfo(prefillData);
  const { sections } = reviewInfoHelper(
    mappedCustomerInfo,
    reactivationApplicationId,
  );

  const goToReactivation = () => {
    router.push('/reactivation');
  };

  useBackHandler(goToReactivation);

  useStepStartedFocusAnalytics(
    reactivationApplicationId
      ? {
          analyticsId: 'reviewInfoStepStarted',
          customAttributes: {
            step_name: 'review_information',
            reactivation_flow: 'true',
            reactivation_id: reactivationApplicationId,
          },
        }
      : undefined,
  );

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={goToReactivation} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={prefillDataFetching}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          prefillDataError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('errorTitle')}
              messageBody={translatedText('errorMessageBody')}
              primaryButton={{
                title: translatedText('buttons.retry'),
                onPress: () => {
                  void refetchPrefillData();
                },
              }}
              secondaryButton={{
                title: translatedText('buttons.cancel'),
                onPress: () => {
                  router.push('/reactivation');
                },
              }}
            />
          ) : null
        }
      />
      <View style={styles.textContainer}>
        {validationError ? (
          <InlineAlert
            alertType={InlineAlertType.Error}
            bodyText={validationError}
            iconKeyType="ERROR"
            onDismiss={() => {}}
          />
        ) : null}
        <TitleXLText>{translatedText('reviewInfoTitle')}</TitleXLText>
        <BodyBaseText>{translatedText('reviewInfoDescription')}</BodyBaseText>
      </View>
      <SectionedContentScreen model={{ sections }} />
      <View
        style={[
          styles.buttonWrapper,
          { paddingBottom: insets.bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={translatedText('buttons.next')}
          analyticsId="nextButtonPressed"
          customAttributes={{
            reactivation_flow: 'true',
            reactivation_id: reactivationApplicationId,
            step_name: 'review_information',
          }}
          onPress={() => {
            if (!mappedCustomerInfo?.citizenship) {
              setValidationError(translatedText('validationErrorMessage'));
              return;
            }
            router.replace('/reactivation/legal');
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'space-between',
    zIndex: 1,
  },
  textContainer: {
    display: 'flex',
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
});

export default ReviewInfo;
